import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import BrandSelect from '../ui/BrandSelect';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Actions as CONN_ACTIONS } from '../../store/actions/connectionActions';
import { adminToolbarSelect } from '../utils/ReactSelectStyles';

const SelectExternalAccountStep = ({
  step,
  connectorId,
  externalAccounts,
  loading,
  getExternalAccounts,
  selectExternalAccount,
}) => {
  useEffect(() => {
    getExternalAccounts(connectorId, step.optionsUrl);
  }, [connectorId, step.optionsUrl]);

  const options = useMemo(
    () =>
      (externalAccounts?.accounts || []).map((a) => ({
        value: a.id,
        label: a.label,
        account: a,
      })),
    [externalAccounts],
  );

  const selected = options.find((o) => o.value === externalAccounts?.selectedId) || null;
  const uiLabel = step.uiLabel || externalAccounts?.uiLabel || 'Organisation';

  // Always show the selector (even with one option) for a consistent switch/reconfirm experience.
  return (
    <div className='flex flex-col gap-3 sm:flex-row sm:items-end'>
      <div className='flex-1 min-w-0'>
        <label className='mb-1 block text-sm font-medium text-foreground'>{uiLabel}</label>
        <BrandSelect
          styles={adminToolbarSelect}
          options={options}
          value={selected}
          isLoading={loading?.externalAccounts}
          placeholder={`Select ${uiLabel.toLowerCase()}`}
          onChange={(opt) => {
            if (opt?.account) selectExternalAccount(connectorId, opt.account, step.submitUrl);
          }}
        />
      </div>
      <Button
        variant='outline'
        loading={loading?.externalAccounts}
        onClick={() => getExternalAccounts(connectorId, step.optionsUrl)}>
        Refresh
      </Button>
    </div>
  );
};

const SetupSteps = ({
  connectorId,
  setupSteps,
  externalAccounts,
  loading,
  getExternalAccounts,
  selectExternalAccount,
}) => {
  const steps = setupSteps || [];
  if (!steps.length) return null;

  return (
    <Card className='bg-card text-card-foreground border-border'>
      <CardHeader>
        <CardTitle className='text-base'>Finish setup</CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        {steps.map((step) => {
          if (step.type === 'selectExternalAccount') {
            return (
              <SelectExternalAccountStep
                key={step.id || step.type}
                step={step}
                connectorId={connectorId}
                externalAccounts={externalAccounts}
                loading={loading}
                getExternalAccounts={getExternalAccounts}
                selectExternalAccount={selectExternalAccount}
              />
            );
          }
          return (
            <p key={step.id || step.type} className='text-sm text-muted-foreground'>
              Unsupported setup step type: {step.type}
            </p>
          );
        })}
      </CardContent>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  externalAccounts: state.connections?.externalAccounts,
  loading: state.connections?.loading,
});

const mapDispatchToProps = (dispatch) => ({
  getExternalAccounts: (id, url) => dispatch(CONN_ACTIONS.getExternalAccounts(id, url)),
  selectExternalAccount: (id, account, url) => dispatch(CONN_ACTIONS.selectExternalAccount(id, account, url)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SetupSteps);
